import { useId, useState } from "react";
import { ApiError, api, type RefundAsked, type RefundRequestState } from "./api.ts";
import { Modal } from "./Dialog.tsx";
import { useGrow } from "./hooks.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";

// Asking for a refund on the web (features/paying.md, "Refunds"): where something bought can be refunded
// now (the server's check, never the page's), its row says until when and why and offers Request a refund;
// once asked, it says where the request is. The dialog says what a refund takes back, takes an optional
// word for the person at Tripmo, and shows the server's answer: in, or why not.

export type RefundOffer = { until: string; why: string } | null;
export type RefundTarget = { type: RefundRequestState["target_type"]; id: string };

const LONG = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long" });
const LONG_YEAR = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });
const SHORT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
// "1 November", or "4 January 2027" in another year.
export const untilDay = (iso: string) => {
  const at = new Date(iso);
  return (at.getFullYear() === new Date().getFullYear() ? LONG : LONG_YEAR).format(at);
};

// Where a thing's refund stands, in a line, or null when there's nothing to say. `settled`: the row says
// it's refunded already.
export function refundLine(offer: RefundOffer, request: RefundRequestState | null, settled = false): string | null {
  if (request?.status === "open")
    return request.eligible
      ? `Refund requested ${SHORT.format(new Date(request.created_at))}: a person at Tripmo checks it, usually within a few days`
      : `Refund requested ${SHORT.format(new Date(request.created_at))}: a person at Tripmo will look at it`;
  if (request?.status === "approved") return "Refund approved: it's on its way to your card";
  if (request?.status === "refunded") return settled ? null : "Refunded to your card";
  const declined =
    request?.status === "rejected" ? `Refund declined: ${request.note ?? "write to us to ask why"}` : null;
  if (offer && !settled) {
    const line = `Refundable until ${untilDay(offer.until)}: ${offer.why}`;
    return declined ? `${declined}. ${line}` : line;
  }
  return declined;
}

// The row's action: Request a refund while it can be, Withdraw while a request is open, else nothing.
export function RefundAction({
  target,
  what,
  offer,
  request,
  takesBack,
  className,
  onChanged,
}: {
  target: RefundTarget;
  // What it is ("Trip Pass", "+5 spots on Kyoto"), for the dialog's title line.
  what: string;
  offer: RefundOffer;
  request: RefundRequestState | null;
  // What a refund takes back, in a sentence ("The pass goes from Your trips.").
  takesBack: string;
  className?: string;
  onChanged: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (request?.status === "open")
    return (
      <span className={`tm-refund-action${className ? ` ${className}` : ""}`}>
        <button
          type="button"
          className="tm-link-button callout"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.withdrawRefund(request.id);
              onChanged();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Withdrawing…" : "Withdraw request"}
        </button>
        {error && (
          <span className="caption tm-error" role="alert">
            {error}
            <ReportThis />
          </span>
        )}
      </span>
    );
  if (!offer || request?.status === "approved") return null;
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className={`tm-refund-button${className ? ` ${className}` : ""}`}
        onClick={() => setAsking(true)}
      >
        Request a refund
      </Button>
      {asking && (
        <RefundDialog
          target={target}
          what={what}
          offer={offer}
          takesBack={takesBack}
          onClose={(changed) => {
            setAsking(false);
            if (changed) onChanged();
          }}
        />
      )}
    </>
  );
}

export function RefundDialog({
  target,
  what,
  offer,
  takesBack,
  onClose,
}: {
  target: RefundTarget;
  what: string;
  offer: { until: string; why: string };
  takesBack: string;
  onClose: (changed: boolean) => void;
}) {
  const [reason, setReason] = useState("");
  const growReason = useGrow(reason);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<RefundAsked | null>(null);
  const reasonId = useId();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setAnswer(await api.requestRefund(target, reason.trim() || undefined));
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={answer ? (answer.eligible ? "Refund requested" : "Request kept") : "Request a refund"}
      busy={busy}
      focus={answer ? ".tm-refund-done" : `[id="${reasonId}"]`}
      onCancel={() => onClose(answer !== null)}
      className="tm-refund-dialog"
    >
      {({ describedBy }) =>
        answer ? (
          <>
            <p id={describedBy} className="callout" role="status">
              {answer.message}
            </p>
            <div className="tm-dialog-actions">
              <Button variant="primary" className="tm-refund-done" onClick={() => onClose(true)}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form className="tm-refund-form" onSubmit={submit} noValidate>
            <div id={describedBy} className="tm-dialog-body">
              <p className="callout">
                <strong>{what}</strong> · refundable until {untilDay(offer.until)}: {offer.why}.
              </p>
              <p className="callout tm-muted">
                A person at Tripmo checks it, usually within a few days, and the money goes back to the card you paid
                with. {takesBack}
              </p>
            </div>
            <label className="sv-field" htmlFor={reasonId}>
              <span className="sv-field-label">Anything we should know? (optional)</span>
              <textarea
                ref={growReason}
                id={reasonId}
                className="sv-field-input tm-refund-reason"
                value={reason}
                maxLength={1000}
                rows={3}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            {error && (
              <p className="callout tm-error" role="alert">
                {error}
                <ReportThis />
              </p>
            )}
            <div className="tm-dialog-actions">
              <Button variant="secondary" disabled={busy} onClick={() => onClose(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? "Requesting…" : "Request refund"}
              </Button>
            </div>
          </form>
        )
      }
    </Modal>
  );
}
